import { type FC, useCallback } from 'react';

import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { type BrandedSearchPageTemplate } from '@vakantiesnl/services/src/contentful/brandedSearchTemplate';

import { useStyles } from './HeaderDescription.style';

interface Props {
	description: string;
	readMoreRef: React.RefObject<HTMLDivElement>;
	readMoreText: string | null;
	className?: string;
	readMoreLink?: BrandedSearchPageTemplate['readMoreLink'];
}

export const HeaderDescription: FC<Props> = ({ description, readMoreText, readMoreLink, readMoreRef, className }) => {
	const { classes, cx } = useStyles();

	const handleTrackReadMore = useCallback(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/search').then(({ readMoreClick }) =>
				track(
					readMoreLink
						? readMoreClick({ label: readMoreLink.title, link: readMoreLink.link })
						: readMoreClick({ label: readMoreText, link: null }),
				),
			),
		);
	}, [readMoreLink, readMoreText]);

	const onClickReadMore = useCallback(() => {
		handleTrackReadMore();
		if (window && window.scrollTo && readMoreRef) {
			window.scrollTo({
				top: readMoreRef?.current?.offsetTop,
				behavior: 'smooth',
			});
		}
	}, [handleTrackReadMore, readMoreRef]);

	return (
		<div className={cx(classes.descriptionContainer, className)}>
			<Typography variant="bodyMd" as="p" className={classes.description}>
				{description}
			</Typography>
			{(readMoreText || readMoreLink) &&
				(readMoreLink ? (
					<Link className={classes.readMoreButton} href={readMoreLink.link} onClick={handleTrackReadMore}>
						{readMoreLink.title}
					</Link>
				) : (
					<Link className={classes.readMoreButton} onClick={onClickReadMore}>
						{readMoreText}
					</Link>
				))}
		</div>
	);
};
